import React, { useEffect, useMemo, useState } from "react";
import { doc, onSnapshot, setDoc, deleteField } from "firebase/firestore";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { ColorPicker } from "../components/color-picker";
import { db, isFirebaseConfigured } from "../lib/firebase";

// Shared calendar lives in a single Firestore document.
const CALENDAR_ID = "games-with-tim";
const IDENTITY_KEY = "gamesWithTimIdentity"; // localStorage: which slot is "me"
const SLOTS = ["A", "B"];

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Build a local "YYYY-MM-DD" key (avoids UTC off-by-one from toISOString).
const dateKey = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// Format a "YYYY-MM-DD" key for display, e.g. "Sat, Jun 14, 2026".
const formatConfirmed = (key) => {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("default", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

// One small circle showing a participant's status for a day:
// available = solid fill, unavailable = hollow ring with an ✕, in their color.
const StatusDot = ({ color, status }) => {
  if (status === "available") {
    return (
      <span
        className="inline-block h-3 w-3 rounded-full"
        style={{ backgroundColor: color }}
      />
    );
  }
  if (status === "unavailable") {
    return (
      <span
        className="inline-flex h-3 w-3 items-center justify-center rounded-full border-2 text-[8px] font-bold leading-none"
        style={{ borderColor: color, color }}
      >
        ✕
      </span>
    );
  }
  return null;
};

const GamesWithTim = () => {
  const [calendar, setCalendar] = useState({ participants: {}, marks: {} });
  const [loaded, setLoaded] = useState(false);
  const [mySlot, setMySlot] = useState(
    () => localStorage.getItem(IDENTITY_KEY) || null
  );
  const [viewMonth, setViewMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  // Onboarding form state
  const [nameInput, setNameInput] = useState("");
  const [colorInput, setColorInput] = useState("");

  const calRef = useMemo(
    () => (db ? doc(db, "calendars", CALENDAR_ID) : null),
    []
  );

  // Live subscription to the shared document.
  useEffect(() => {
    if (!calRef) return undefined;
    const unsub = onSnapshot(calRef, (snap) => {
      const data = snap.exists() ? snap.data() : {};
      setCalendar({
        participants: data.participants || {},
        marks: data.marks || {},
      });
      setLoaded(true);
    });
    return unsub;
  }, [calRef]);

  const participants = calendar.participants || {};
  const marks = calendar.marks || {};
  const me = mySlot ? participants[mySlot] : null;
  const otherSlot = mySlot === "A" ? "B" : "A";
  const usedColors = SLOTS.map((s) => participants[s]?.color).filter(Boolean);

  const claimSlot = async (slot, name, color) => {
    await setDoc(
      calRef,
      { participants: { [slot]: { name, color } } },
      { merge: true }
    );
    localStorage.setItem(IDENTITY_KEY, slot);
    setMySlot(slot);
  };

  const handleClaim = async () => {
    const name = nameInput.trim();
    if (!name || !colorInput) return;
    // Take the first free slot (A preferred).
    const freeSlot = SLOTS.find((s) => !participants[s]);
    if (!freeSlot) return;
    await claimSlot(freeSlot, name, colorInput);
  };

  const adoptIdentity = (slot) => {
    localStorage.setItem(IDENTITY_KEY, slot);
    setMySlot(slot);
  };

  const switchIdentity = () => {
    localStorage.removeItem(IDENTITY_KEY);
    setMySlot(null);
    setNameInput("");
    setColorInput("");
  };

  const changeMyColor = async (color) => {
    if (!me) return;
    await setDoc(
      calRef,
      { participants: { [mySlot]: { ...me, color } } },
      { merge: true }
    );
  };

  // Click a day: cycle MY status unmarked -> available -> unavailable -> unmarked.
  const cycleDay = async (key) => {
    if (!mySlot) return;
    const current = marks[key]?.[mySlot];
    const next =
      current === undefined || current === null
        ? "available"
        : current === "available"
        ? "unavailable"
        : null; // remove the mark
    await setDoc(
      calRef,
      { marks: { [key]: { [mySlot]: next === null ? deleteField() : next } } },
      { merge: true }
    );
  };

  // Build the grid: leading blanks + each day of the displayed month.
  const grid = useMemo(() => {
    const year = viewMonth.getFullYear();
    const month = viewMonth.getMonth();
    const firstWeekday = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstWeekday; i += 1) cells.push(null);
    for (let d = 1; d <= daysInMonth; d += 1) {
      cells.push(new Date(year, month, d));
    }
    return cells;
  }, [viewMonth]);

  const todayKey = dateKey(new Date());

  // Upcoming days where every claimed person is available — the matches.
  const confirmedDays = useMemo(() => {
    const ps = calendar.participants || {};
    const ms = calendar.marks || {};
    const claimed = SLOTS.filter((s) => ps[s]);
    if (claimed.length === 0) return [];
    const today = dateKey(new Date());
    return Object.keys(ms)
      .filter(
        (key) =>
          key >= today && claimed.every((s) => ms[key][s] === "available")
      )
      .sort();
  }, [calendar]);

  // ---- Render guards ----------------------------------------------------

  if (!isFirebaseConfigured) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="mx-auto w-full max-w-2xl">
          <Card>
            <CardHeader>
              <CardTitle className="text-3xl">Game Night Calendar</CardTitle>
              <CardDescription>Not configured yet</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-muted-foreground">
              <p>
                This page needs Firebase to sync the calendar between devices.
                Add the <code>REACT_APP_FIREBASE_*</code> values (see{" "}
                <code>.env.example</code>) to your local <code>.env</code> and to
                the Vercel project's Environment Variables, then redeploy.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!loaded) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="mx-auto w-full max-w-2xl text-center text-muted-foreground">
          Loading calendar…
        </div>
      </div>
    );
  }

  // ---- Onboarding (no identity on this device yet) ----------------------

  if (!mySlot) {
    const freeSlot = SLOTS.find((s) => !participants[s]);
    const claimedParticipants = SLOTS.filter((s) => participants[s]);
    const canPick = nameInput.trim() && colorInput;
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="mx-auto w-full max-w-2xl space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-3xl">Game Night Calendar</CardTitle>
              <CardDescription>
                Pick who you are so we can track your days.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {claimedParticipants.length > 0 && (
                <div className="space-y-2">
                  <Label>Already set up? That's me:</Label>
                  <div className="flex flex-wrap gap-2">
                    {claimedParticipants.map((s) => (
                      <Button
                        key={s}
                        variant="outline"
                        onClick={() => adoptIdentity(s)}
                        className="gap-2"
                      >
                        <span
                          className="h-3 w-3 rounded-full"
                          style={{ backgroundColor: participants[s].color }}
                        />
                        {participants[s].name}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {freeSlot ? (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Your name</Label>
                    <input
                      id="name"
                      type="text"
                      value={nameInput}
                      onChange={(e) => setNameInput(e.target.value)}
                      placeholder="e.g. Keyoni"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Your color (must differ from the other person)</Label>
                    <ColorPicker
                      selected={colorInput}
                      disabledColors={usedColors}
                      onPick={setColorInput}
                    />
                  </div>
                  <Button onClick={handleClaim} disabled={!canPick}>
                    Start marking days
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Both spots are already taken. If one of them is you, use the
                  button above.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ---- Main calendar ----------------------------------------------------

  const monthLabel = viewMonth.toLocaleString("default", {
    month: "long",
    year: "numeric",
  });
  const goMonth = (delta) =>
    setViewMonth(
      (m) => new Date(m.getFullYear(), m.getMonth() + delta, 1)
    );

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mx-auto w-full max-w-3xl space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-3xl">Game Night Calendar</CardTitle>
            <CardDescription>
              Tap a day to cycle your status: available → unavailable → clear.
              Days where everyone is available get a ⭐.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6 px-3 sm:px-6">
            {/* Who's who + color controls */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-4 text-sm">
                {SLOTS.filter((s) => participants[s]).map((s) => (
                  <div key={s} className="flex items-center gap-2">
                    <span
                      className="h-3 w-3 rounded-full"
                      style={{ backgroundColor: participants[s].color }}
                    />
                    <span className={s === mySlot ? "font-semibold" : ""}>
                      {participants[s].name}
                      {s === mySlot ? " (you)" : ""}
                    </span>
                  </div>
                ))}
              </div>
              <Button variant="ghost" size="sm" onClick={switchIdentity}>
                Not you? Switch
              </Button>
            </div>

            {/* Your color editor */}
            <div className="space-y-2">
              <Label>Your color</Label>
              <ColorPicker
                selected={me?.color}
                disabledColors={[participants[otherSlot]?.color].filter(Boolean)}
                onPick={changeMyColor}
              />
            </div>

            {/* Month nav */}
            <div className="flex items-center justify-between">
              <Button variant="outline" size="icon" onClick={() => goMonth(-1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="text-lg font-semibold">{monthLabel}</div>
              <Button variant="outline" size="icon" onClick={() => goMonth(1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
              {WEEKDAYS.map((w) => (
                <div
                  key={w}
                  className="py-1 text-center text-xs font-medium text-muted-foreground"
                >
                  {w}
                </div>
              ))}
              {grid.map((d, i) => {
                if (!d) return <div key={`blank-${i}`} />;
                const key = dateKey(d);
                const dayMarks = marks[key] || {};
                const isToday = key === todayKey;
                const claimed = SLOTS.filter((s) => participants[s]);
                const allAvailable =
                  claimed.length > 0 &&
                  claimed.every((s) => dayMarks[s] === "available");
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => cycleDay(key)}
                    title={`${key}\nYour status: ${
                      dayMarks[mySlot] || "unset"
                    }`}
                    className={`relative flex min-h-[3.25rem] flex-col items-center justify-between rounded-md border p-0.5 text-sm transition-colors hover:bg-accent sm:min-h-[3.5rem] sm:p-1 ${
                      isToday ? "border-foreground" : "border-border"
                    } ${
                      allAvailable ? "ring-2 ring-emerald-500" : ""
                    }`}
                  >
                    <span className="self-start text-xs">{d.getDate()}</span>
                    {allAvailable && (
                      <Star className="absolute right-0.5 top-0.5 h-3 w-3 fill-emerald-500 text-emerald-500" />
                    )}
                    <span className="mb-0.5 flex items-center gap-1">
                      {claimed.map((s) => (
                        <StatusDot
                          key={s}
                          color={participants[s].color}
                          status={dayMarks[s]}
                        />
                      ))}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Legend */}
            <div className="space-y-1 border-t pt-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-2">
                <StatusDot color="#64748b" status="available" />
                <span>Solid = available</span>
              </div>
              <div className="flex items-center gap-2">
                <StatusDot color="#64748b" status="unavailable" />
                <span>Hollow with ✕ = unavailable</span>
              </div>
              <div className="flex items-center gap-2">
                <Star className="h-3 w-3 fill-emerald-500 text-emerald-500" />
                <span>Everyone available — a candidate game night!</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Confirmed game nights — days everyone is available */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Confirmed game nights</CardTitle>
            <CardDescription>
              Upcoming days you're all free — your candidate nights to play.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {confirmedDays.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No matching days yet. Once everyone marks the same day as
                available, it'll show up here.
              </p>
            ) : (
              <ul className="space-y-2">
                {confirmedDays.map((key) => (
                  <li key={key} className="flex items-center gap-2 text-sm">
                    <Star className="h-4 w-4 shrink-0 fill-emerald-500 text-emerald-500" />
                    <span>{formatConfirmed(key)}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default GamesWithTim;
